import type { GameStatus, GamePhase, PlayerId } from '../types';
import type { Board } from './Board';
import { createBoard } from './Board';
import type { LetterPool } from './LetterPool';
import { createLetterPool } from './LetterPool';
import type { Player } from './Player';
import { createPlayer } from './Player';
import type { Letter } from './Letter';
import { generateId } from '../../utils/uuid';

export interface RackLetter {
  letter: Letter;
  x: number; // Physics position
  y: number; // Physics position
}

export interface CueBallPosition {
  x: number; // Physics position
  y: number; // Physics position
}

export type PocketSize = 'small' | 'medium' | 'large';
export type TimerMode = 'off' | '1min' | '2min' | '5min';

export interface GameSettings {
  pocketSize: PocketSize; // Pocket size (affects difficulty)
  showTargetLine: boolean; // Show aim line when shooting
  timerMode: TimerMode; // Turn timer for board phase
  numPlayers: number; // Number of players (2-4)
}

export interface GameRules {
  returnGutteredLetters: boolean; // Whether to return guttered/out-of-bounds letters to pool
  stalemateThreshold: number; // Number of consecutive passes before stalemate (per player)
}

export interface Game {
  id: string; // Unique game identifier (UUID)
  status: GameStatus; // current | paused | completed
  currentPlayerId: PlayerId; // 1-indexed player ID
  phase: GamePhase; // pool | board | menu
  board: Board;
  letterPool: LetterPool;
  players: Player[]; // 1-6 players supported
  currentRackLetters: RackLetter[]; // Letters currently on the pool table with positions (persists until rack is cleared)
  cueBallPosition: CueBallPosition | null; // Cue ball position (null = use default starting position)
  consecutivePassesPerPlayer: Record<PlayerId, number>; // Tracks consecutive turns without playing a word (for stalemate detection)
  settings: GameSettings; // User-configurable game settings
  rules: GameRules; // Configurable game rules
  turnNumber: number; // Increments each turn
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Create a new game with 1-6 players
 */
export function createGame(
  playerNames: string[] = ['Player 1', 'Player 2'],
  boardSeed?: string | number
): Game {
  // Validate player count
  if (playerNames.length < 2 || playerNames.length > 4) {
    throw new Error('Game requires 2-4 players');
  }
  
  // Create players with 1-indexed IDs
  const players = playerNames.map((name, index) => 
    createPlayer(index + 1, name)
  );
  
  // Initialize consecutive passes tracking for all players
  const consecutivePassesPerPlayer: Record<PlayerId, number> = {};
  players.forEach(player => {
    consecutivePassesPerPlayer[player.id] = 0;
  });
  
  return {
    id: generateId(),
    status: 'current',
    currentPlayerId: 1,
    phase: 'menu',
    board: createBoard(boardSeed),
    letterPool: createLetterPool(),
    players,
    currentRackLetters: [], // Empty rack at start
    cueBallPosition: null, // Use default starting position
    consecutivePassesPerPlayer,
    settings: {
      pocketSize: 'medium', // Default pocket size
      showTargetLine: false, // Default: no aim line
      timerMode: 'off', // Default: no timer
      numPlayers: playerNames.length, // Track number of players
    },
    rules: {
      returnGutteredLetters: true, // Default: return letters to pool
      stalemateThreshold: 5, // Default: 5 consecutive passes per player
    },
    turnNumber: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Get the current player
 */
export function getCurrentPlayer(game: Game): Player {
  const player = game.players.find(p => p.id === game.currentPlayerId);
  if (!player) {
    throw new Error(`Player with ID ${game.currentPlayerId} not found`);
  }
  return player;
}

/**
 * Get all other players (excluding current player)
 */
export function getOtherPlayers(game: Game): Player[] {
  return game.players.filter(p => p.id !== game.currentPlayerId);
}

/**
 * Get a specific player by ID
 */
export function getPlayer(game: Game, playerId: PlayerId): Player | undefined {
  return game.players.find(p => p.id === playerId);
}

/**
 * Update a player in the game
 */
export function updatePlayer(game: Game, playerId: PlayerId, updatedPlayer: Player): Game {
  const newPlayers = game.players.map(p => 
    p.id === playerId ? updatedPlayer : p
  );
  
  return {
    ...game,
    players: newPlayers,
    updatedAt: new Date(),
  };
}

/**
 * Switch to the next player (cycles through all players)
 */
export function switchPlayer(game: Game): Game {
  const currentIndex = game.players.findIndex(p => p.id === game.currentPlayerId);
  const nextIndex = (currentIndex + 1) % game.players.length;
  const nextPlayerId = game.players[nextIndex].id;
  
  return {
    ...game,
    currentPlayerId: nextPlayerId,
    turnNumber: game.turnNumber + 1,
    cueBallPosition: null, // Reset so new player can reposition cue ball
    updatedAt: new Date(),
  };
}

/**
 * Update game phase
 */
export function updatePhase(game: Game, phase: GamePhase): Game {
  return {
    ...game,
    phase,
    updatedAt: new Date(),
  };
}

/**
 * Update game status
 */
export function updateStatus(game: Game, status: GameStatus): Game {
  return {
    ...game,
    status,
    updatedAt: new Date(),
  };
}

/**
 * Increment consecutive passes for a player
 */
export function incrementConsecutivePasses(game: Game, playerId: PlayerId): Game {
  return {
    ...game,
    consecutivePassesPerPlayer: {
      ...game.consecutivePassesPerPlayer,
      [playerId]: (game.consecutivePassesPerPlayer[playerId] || 0) + 1,
    },
    updatedAt: new Date(),
  };
}

/**
 * Reset consecutive passes for a player (called when they play a word)
 */
export function resetConsecutivePasses(game: Game, playerId: PlayerId): Game {
  return {
    ...game,
    consecutivePassesPerPlayer: {
      ...game.consecutivePassesPerPlayer,
      [playerId]: 0,
    },
    updatedAt: new Date(),
  };
}

/**
 * Check if game is in stalemate (all players have passed threshold consecutive turns)
 */
export function isStalemate(game: Game): boolean {
  const threshold = game.rules.stalemateThreshold;
  
  // Check if all players have at least threshold consecutive passes
  return game.players.every(player => 
    (game.consecutivePassesPerPlayer[player.id] || 0) >= threshold
  );
}

/**
 * Check if the game is over
 */
export function isGameOver(game: Game): boolean {
  return game.status === 'completed' || game.letterPool.isExhausted || isStalemate(game);
}

/**
 * Get the winner (or null if game not over or tie)
 */
export function getWinner(game: Game): Player | null {
  if (!isGameOver(game)) return null;
  
  // Find player(s) with highest score
  const maxScore = Math.max(...game.players.map(p => p.score));
  const winners = game.players.filter(p => p.score === maxScore);
  
  // Return winner only if there's exactly one
  return winners.length === 1 ? winners[0] : null;
}

/**
 * Get all players sorted by score (descending)
 */
export function getPlayersByScore(game: Game): Player[] {
  return [...game.players].sort((a, b) => b.score - a.score);
}
